import React, { useEffect, useRef, useState } from 'react';
import ProjectCell from '../ProjectCell';
import TaskTicketLinks from '../TaskTicketLinks';
import BlockEditor from './BlockEditor';
import { NtIcon } from '../NotionUI';
import { getProjectTask, updateProjectTask, addProjectTaskComment, deleteProjectTaskComment, errorMessage } from '../../services/api';
import { useLanguage } from '../../i18n/LanguageContext';
import { fmtDateTime } from '../../utils/format';
import { TYPE_ICONS } from '../../utils/projectView';

const SAVE_DELAY = 800;

/**
 * Pagina di un'attività (aperta sopra la tabella): titolo, proprietà, corpo
 * a blocchi con salvataggio automatico, ticket collegati e commenti.
 */
function TaskPeek({ task, allTasks, columns, users, user, backToTicket, onClose, onPatch, onDelete, onTaskReplace, onTicketsChange, onOpenTask, onAddSubtask, onSetParent, onError }) {
  const { t } = useLanguage();
  const [blocks, setBlocks] = useState([]);
  const [comments, setComments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [sending, setSending] = useState(false);
  const saveTimer = useRef(null);
  const pending = useRef(null); // corpo in attesa di salvataggio

  const flush = () => {
    clearTimeout(saveTimer.current);
    if (pending.current) {
      const body = pending.current;
      pending.current = null;
      updateProjectTask(task.id, { body }).catch((err) => onError(errorMessage(err, t('projectDetail.saveError'))));
    }
  };

  useEffect(() => {
    let cancelled = false;
    getProjectTask(task.id)
      .then((data) => {
        if (cancelled) return;
        setBlocks(data.task.body || []);
        setComments(data.comments || []);
        setLoaded(true);
      })
      .catch((err) => onError(errorMessage(err, t('projectDetail.loadError'))));
    return () => {
      cancelled = true;
      flush(); // chiudendo la pagina il corpo modificato non va perso
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  const changeBody = (next) => {
    setBlocks(next);
    pending.current = next;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, SAVE_DELAY);
  };

  const sendComment = async () => {
    const body = commentText.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const data = await addProjectTaskComment(task.id, body);
      setComments(data.comments);
      setCommentText('');
    } catch (err) {
      onError(errorMessage(err, t('projectDetail.saveError')));
    } finally {
      setSending(false);
    }
  };

  const removeComment = async (comment) => {
    try {
      const data = await deleteProjectTaskComment(task.id, comment.id);
      setComments(data.comments);
    } catch (err) {
      onError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  const children = allTasks.filter((x) => x.parent_id === task.id);
  const parent = allTasks.find((x) => x.id === task.parent_id);
  let depth = 0;
  for (let cur = parent; cur && depth < 5; cur = allTasks.find((x) => x.id === cur.parent_id)) depth += 1;

  const initials = (c) => `${(c.user_name || '?')[0] || ''}${(c.user_surname || '')[0] || ''}`.toUpperCase();

  return (
    <>
      <div className="pd-modal-overlay" onClick={onClose} />
      <div className="pd-modal pd-peek" role="dialog" aria-label={task.title || t('projectDetail.untitled')}>
        {backToTicket && (
          <button className="tpl-back" type="button" onClick={backToTicket.go}>← {backToTicket.label}</button>
        )}
        <div className="pd-peek-head">
          <input
            key={`${task.id}:${task.title}`}
            className="pd-title pd-modal-title"
            defaultValue={task.title}
            placeholder={t('projectDetail.untitled')}
            autoFocus={!task.title}
            aria-label={t('projectDetail.taskTitle')}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            onBlur={(e) => e.target.value.trim() !== task.title && onPatch(task.id, { title: e.target.value.trim() })}
          />
          <button type="button" className="pd-peek-close" onClick={onClose} aria-label={t('projectDetail.close')} title={t('projectDetail.close')}>×</button>
        </div>

        <div className="pd-modal-fields">
          {columns.filter((c) => c.type !== 'title').map((column) => (
            <div key={column.id} className="pd-modal-field">
              <span className="nt-th nt-muted">
                <NtIcon name={TYPE_ICONS[column.type]} />
                {column.name}
              </span>
              <ProjectCell
                task={task}
                column={column}
                users={users}
                allTasks={allTasks}
                onSave={(value) => onPatch(task.id, { data: { [String(column.id)]: value } })}
                onTaskReplace={onTaskReplace}
                onError={onError}
              />
            </div>
          ))}
        </div>

        <TaskTicketLinks key={task.id} task={task} onChange={onTicketsChange} onError={onError} />

        <div className="pd-peek-section">
          {parent && (
            <div className="pd-link-item">
              <span className="nt-muted">{t('projectDetail.parentTask')}:</span>
              <button type="button" className="pd-link-btn" onClick={() => onOpenTask(parent.id)}>{parent.title || t('projectDetail.untitled')}</button>
              <button type="button" className="nt-ghost-btn" onClick={() => onSetParent(task.id, null)}>{t('projectDetail.makeTopLevel')}</button>
            </div>
          )}
          <div className="pd-links-title">{t('projectDetail.subtasks')}{children.length > 0 ? ` (${children.length})` : ''}</div>
          {children.map((child) => (
            <div key={child.id} className="pd-link-item">
              <button type="button" className="pd-link-btn" onClick={() => onOpenTask(child.id)}>{child.title || t('projectDetail.untitled')}</button>
            </div>
          ))}
          {depth < 2 && <button type="button" className="nt-ghost-btn" onClick={() => onAddSubtask(task.id)}>+ {t('projectDetail.addSubtask')}</button>}
        </div>

        <div className="pd-peek-section">
          {loaded ? <BlockEditor blocks={blocks} onChange={changeBody} /> : <div className="loading">{t('common.loading')}</div>}
        </div>

        <div className="pd-peek-section">
          <div className="pd-links-title">{t('projectDetail.comments')}{comments.length > 0 ? ` (${comments.length})` : ''}</div>
          {comments.map((comment) => (
            <div key={comment.id} className="pd-comment">
              <span className="avatar avatar-sm avatar-empty" aria-hidden="true">{initials(comment)}</span>
              <div className="pd-comment-main">
                <div className="pd-comment-head">
                  <strong>{[comment.user_name, comment.user_surname].filter(Boolean).join(' ') || t('projectDetail.unknownUser')}</strong>
                  <span className="nt-muted">{fmtDateTime(comment.created_at)}</span>
                  {comment.user_id === user.id && (
                    <button type="button" className="pd-row-delete pd-comment-delete" aria-label={t('projectDetail.deleteComment')} onClick={() => removeComment(comment)}>×</button>
                  )}
                </div>
                <div className="pd-comment-body">{comment.body}</div>
              </div>
            </div>
          ))}
          <div className="pd-comment-form">
            <textarea
              rows={2}
              value={commentText}
              placeholder={t('projectDetail.commentPlaceholder')}
              aria-label={t('projectDetail.commentPlaceholder')}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => (e.ctrlKey || e.metaKey) && e.key === 'Enter' && sendComment()}
            />
            <button type="button" className="btn" disabled={!commentText.trim() || sending} onClick={sendComment}>{t('projectDetail.sendComment')}</button>
          </div>
        </div>

        <div className="pd-peek-footer">
          <button type="button" className="pd-peek-delete" onClick={() => onDelete(task)}>
            <NtIcon name="trash" /> {t('projectDetail.deleteTask')}
          </button>
          <button type="button" className="btn" onClick={onClose}>{t('projectDetail.close')}</button>
        </div>
      </div>
    </>
  );
}

export default TaskPeek;
